import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../store';
import Loader from '../../../shared/loader/loader';
import './current-policy.scss';
import InfoIcon from '@svg/exclamation.svg?react';
import { Tooltip } from 'antd';
import fwVersionService from '../../../../services/fw-version.service';
import systemConfigurationService from '../../../../services/system-configuration.service';
import { InactiveOptionPolicySummary, PolicyItem, PolicySummaryForDashboardDto } from '@src/dtos/version.dto';
import { PolicyMode, ProtectionMenuNameOptions } from '@src/utils/enums';
import { isEqual } from 'lodash';
import { isVersionActive } from '@src/utils/versionUtils';
import { AdvancedProtectionRow } from '../../advanced-protection-row/advanced-protection-row';
import { TryAgainMessage } from 'src/components/shared/try-again-message/try-again-message';
import { PolicyItemRenderer } from '../../../shared/label-renderer/label-renderer';
import { SHORT_MONTH_DAY_YEAR_FORMAT } from '@src/utils/datesUtils';
import { Link } from 'react-router-dom';
import { AdvancedProtectionStatus } from '../../advanced-protection-row/advanced-protection-status';

export const CurrentPolicy: React.FC = observer(() => {
    const [policySummary, setPolicySummary] = useState<PolicySummaryForDashboardDto | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isCountLoading, setIsCountLoading] = useState<boolean>(true);
    const [countIP, setCountIP] = useState<number | null>(null);
    const [versionList, setVersionList] = useState<any>();
    const [hasError, setHasError] = useState<boolean>(false);
    const { customerStore, fwVersionStore } = useStores();

    const isSmart = useMemo(() =>
        customerStore.policyMode === PolicyMode.SMART_POLICY && fwVersionStore.isOneVersionActive,
        [customerStore.policyMode, fwVersionStore.isOneVersionActive]);

    useEffect(() => {
        setVersionList(fwVersionStore.fwVersions);
    }, []);

    const latestActiveIsChanged = () => {
        const oldActiveVersion = versionList?.find((version: any) => isVersionActive(version));
        const newActiveVersion = fwVersionStore.fwVersions?.find((version: any) => isVersionActive(version));
        return !isEqual(oldActiveVersion, newActiveVersion);
    };

    useEffect(() => {
        setHasError(false);
        if (latestActiveIsChanged()) {
            setIsLoading(true);
            fwVersionService.getPolicySummaryForDashboard(customerStore.customerId, customerStore.serviceId)
                .then(res => {
                    setPolicySummary(res);
                    setVersionList(fwVersionStore.fwVersions);
                })
                .catch(() => {
                    setPolicySummary(null);
                    setHasError(true);
                })
                .finally(() => setIsLoading(false));

            systemConfigurationService.getCurrentIPBlockedCount().then(res => {
                setCountIP(res);
                setIsCountLoading(false);
            });
        }
    }, [fwVersionStore.fwVersions]);

    const createInactiveStatus = (inactiveStatus?: InactiveOptionPolicySummary) => inactiveStatus && <span className="current-policy-inactive-status">{inactiveStatus}</span>;


    const typePath: Record<string, { type: ProtectionMenuNameOptions }> = useMemo(() => {
        return {
            ['Countries']: {
                type: ProtectionMenuNameOptions.GEO_FILTERING
            },
            ['Web Filtering']: {
                type: ProtectionMenuNameOptions.WEB_FILTERING
            },
            ['Apps']: {
                type: ProtectionMenuNameOptions.APPS
            },
            ['Services']: {
                type: ProtectionMenuNameOptions.SERVICES
            },
            ['Premium Threat Protection']: {
                type: ProtectionMenuNameOptions.PROTECTION_PLUS
            }
        };
    }, []);

    const path = useMemo(() => `/${customerStore.customerId}/${customerStore.serviceId}/protection/version/${fwVersionStore.selectedVersionId}`
    , [customerStore.customerId, customerStore.serviceId, fwVersionStore.selectedVersionId]);

    const hasOnlyOneVersionForSmartPolicy = useMemo(() =>
            customerStore.policyMode === PolicyMode.SMART_POLICY && fwVersionStore.isOneVersionActive,
        [customerStore.policyMode, fwVersionStore.isOneVersionActive, customerStore.getSmartPolicyOffsetFromReady]);

    const Content = () => {
        if(hasError) return <TryAgainMessage />;
        return <>
            <div className="current-policy-list">
                <div className="current-policy-list-header">
                    <div className="cell type">TYPE</div>
                    <div className="cell blocked">BLOCKED</div>
                    <div className="cell allowed">ALLOWED</div>
                </div>
                <div className="current-policy-list-content">
                    {policySummary?.policyTypes?.map((item: PolicyItem) =>
                        <div className="current-policy-list-row" key={item.policyType.toLowerCase()}>
                            <div className={`cell type`}>
                                <Link key={item.policyType} className={`policy-type-item ${hasOnlyOneVersionForSmartPolicy ? 'disabled' : ''}`}
                                      to={`${path}/${typePath[item.policyType].type}${fwVersionStore.calculateSubProtectionPath(typePath[item.policyType].type)}`} >
                                    <div>{item.policyType}</div>
                                </Link>
                                {createInactiveStatus(item.inactiveStatus)}
                            </div>
                            <div className="cell blocked">{isSmart ? '--' : item.blocked.toLocaleString()}</div>
                            <div className="cell allowed">{item.allowed.toLocaleString()}</div>
                        </div>)}
                    {policySummary?.advancedThreatFeatures &&
                        <div className="current-policy-advanced">
                            <Link className={`policy-type-item ${hasOnlyOneVersionForSmartPolicy ? 'disabled' : ''}`}
                                  to={`${path}/protection-plus`}>
                                { customerStore.isAdvancedConnection ? 'DNS Protection' : 'Premium Threat Protection' }
                            </Link>
                            {customerStore.isAdvancedConnection ?
                                <><div>set to</div><AdvancedProtectionStatus value={policySummary.advancedThreatFeatures.dns} /></> :
                                <Tooltip placement="top" overlayClassName="advanced-protection-tooltip"
                                    title={<div className="advanced-protection-content">
                                        <div className="advanced-protection-title">
                                            Current settings
                                        </div>
                                        <div className="advanced-protection-table">
                                            <AdvancedProtectionRow type="Virus" value={policySummary.advancedThreatFeatures.anti_virus} />
                                            <AdvancedProtectionRow type="Spyware" value={policySummary.advancedThreatFeatures.anti_spyware} />
                                            <AdvancedProtectionRow type="DNS" value={policySummary.advancedThreatFeatures.dns} />
                                            <AdvancedProtectionRow type="Vulnerability" value={policySummary.advancedThreatFeatures.vulnerability_protection} />
                                        </div>
                                    </div>}>
                                    <InfoIcon />
                                </Tooltip>}
                        </div>
                    }
                </div>
            </div>
            <div className="current-policy-bottom">
                <div className="name">
                    <div>Dynamic Defense Shield</div>
                    {createInactiveStatus(policySummary?.ddShieldInactiveStatus)}
                </div>
                {isCountLoading ? <Loader type="component" /> : <div className="count">{countIP?.toLocaleString()} IP's blocked</div>}
            </div>
        </>;
    };

    return (
        <div className="current-policy">
            <div className="current-policy-header">
                {fwVersionStore.getActiveVersion && <PolicyItemRenderer data={fwVersionStore.getActiveVersion} dateFormat={SHORT_MONTH_DAY_YEAR_FORMAT}/>}
            </div>
            {isLoading ? <Loader type="component" /> : <Content />}
        </div>
    );
});